import { FormulaOperation } from './formula-operation';

export interface OperationDefinition {
  name: string;
  description: string;
  example?: string;
  syntax?: string;
}

export const FORMULA_OPERATION_DEFINITIONS: Record<keyof typeof FormulaOperation, OperationDefinition> = {
  Equals: {
    name: 'Equals',
    description: 'formulas.formulaDescriptions.equals',
    syntax: 'Equals(value1, value2)',
    example: 'Equals(Amount, 100)',
  },
  Eq: {
    name: 'EqualsAlias',
    description: 'formulas.formulaDescription.equals',
    syntax: 'Eq(value1, value2)',
    example: 'Eq(Status, "Active")',
  },
  NotEquals: {
    name: 'NotEquals',
    description: 'formulas.formulaDescriptions.notEquals',
    syntax: 'NotEquals(value1, value2)',
    example: 'NotEquals(Status, "Inactive")',
  },
  Neq: {
    name: 'Neq',
    description: 'formulas.formulaDescriptions.notEquals',
    syntax: 'Neq(value1, value2)',
    example: 'Neq(Status, "Inactive")',
  },
  GreaterThan: {
    name: 'GreaterThan',
    description: 'formulas.formulaDescriptions.greaterThan',
    syntax: 'GreaterThan(value1, value2)',
    example: 'GreaterThan(Score, 50)',
  },
  GreaterThanOrEqual: {
    name: 'GreaterThanOrEqual',
    description: 'formulas.formulaDescriptions.greaterThanOrEqual',
    syntax: 'GreaterThanOrEqual(value1, value2)',
    example: 'GreaterThanOrEqual(Age, 18)',
  },
  LessThan: {
    name: 'LessThan',
    description: 'formulas.formulaDescriptions.lessThan',
    syntax: 'LessThan(value1, value2)',
    example: 'LessThan(Price, 1000)',
  },
  LessThanOrEquals: {
    name: 'LessThanOrEquals',
    description: 'formulas.formulaDescriptions.lessThanOrEqual',
    syntax: 'LessThanOrEquals(value1, value2)',
    example: 'LessThanOrEquals(Quantity, 10)',
  },
  Between: {
    name: 'Between',
    description: 'formulas.formulaDescriptions.between',
    syntax: 'Between(value, min, max)',
    example: 'Between(Score, 60, 90)',
  },
  Concat: {
    name: 'Concat',
    description: 'formulas.formulaDescriptions.concat',
    syntax: 'Concat(text1, text2, ...)',
    example: 'Concat(FirstName, " ", LastName)',
  },
  StartsWith: {
    name: 'StartsWith',
    description: 'formulas.formulaDescriptions.startsWith',
    syntax: 'StartsWith(text, prefix)',
    example: 'StartsWith(Name, "Dr.")',
  },
  Contains: {
    name: 'Contains',
    description: 'formulas.formulaDescriptions.contains',
    syntax: 'Contains(text, substring)',
    example: 'Contains(Description, "urgent")',
  },
  DoesNotContain: {
    name: 'NotContains',
    description: 'formulas.formulaDescriptions.notContains',
    syntax: 'NotContains(text, substring)',
    example: 'NotContains(Notes, "obsolete")',
  },
  EndsWith: {
    name: 'EndsWith',
    description: 'Checks if a text ends with a specific character sequence.',
    syntax: 'EndsWith(text, suffix)',
    example: 'EndsWith(FileName, ".pdf")',
  },
  IsEmpty: {
    name: 'IsEmpty',
    description: 'formulas.formulaDescriptions.isEmpty',
    syntax: 'IsEmpty(value)',
    example: 'IsEmpty(Comment)',
  },
  IsNotEmpty: {
    name: 'IsNotEmpty',
    description: 'formulas.formulaDescriptions.isNotEmpty',
    syntax: 'IsNotEmpty(value)',
    example: 'IsNotEmpty(Address)',
  },
  Add: {
    name: 'Add',
    description: 'formulas.formulaDescriptions.add',
    syntax: 'Add(value1, value2)',
    example: 'Add(Price, Tax)',
  },
  Subtract: {
    name: 'Subtract',
    description: 'formulas.formulaDescriptions.subtract',
    syntax: 'Subtract(value1, value2)',
    example: 'Subtract(Total, Discount)',
  },
  Multiply: {
    name: 'Multiply',
    description: 'formulas.formulaDescriptions.multiply',
    syntax: 'Multiply(value1, value2)',
    example: 'Multiply(Quantity, Price)',
  },
  Divide: {
    name: 'Divide',
    description: 'formulas.formulaDescriptions.divide',
    syntax: 'Divide(value1, value2)',
    example: 'Divide(Total, Count)',
  },
  If: {
    name: 'If',
    description: 'formulas.formulaDescriptions.if',
    syntax: 'If(condition, valueIfTrue, valueIfFalse)',
    example: 'If(GreaterThan(Age, 18), "Adult", "Minor")',
  },
  Not: {
    name: 'Not',
    description: 'formulas.formulaDescriptions.not',
    syntax: 'Not(condition)',
    example: 'Not(IsEmpty(Name))',
  },
  DateDiff: {
    name: 'DateDiff',
    description: 'formulas.formulaDescriptions.dateDiff',
    syntax: 'DateDiff(date1, date2, unit)',
    example: 'DateDiff(StartDate, EndDate, "days")',
  },
  DateAdd: {
    name: 'DateAdd',
    description: 'formulas.formulaDescriptions.dateAdd',
    syntax: 'DateAdd(date, amount, unit)',
    example: 'DateAdd(StartDate, 7, "days")',
  },
  IsNull: {
    name: 'IsNull',
    description: 'formulas.formulaDescriptions.isNull',
    syntax: 'IsNull(value)',
    example: 'IsNull(EndDate)',
  },
  IsNotNull: {
    name: 'IsNotNull',
    description: 'formulas.formulaDescriptions.isNotNull',
    syntax: 'IsNotNull(value)',
    example: 'IsNotNull(EndDate)',
  },
  Or: {
    name: 'Or',
    description: 'formulas.formulaDescriptions.or',
    syntax: 'Or(condition1, condition2, ...)',
    example: 'Or(IsEmpty(Phone), IsEmpty(Email))',
  },
  And: {
    name: 'And',
    description: 'formulas.formulaDescriptions.and',
    syntax: 'And(condition1, condition2, ...)',
    example: 'And(GreaterThan(Age, 18), IsNotEmpty(ID))',
  },
  Then: {
    name: 'Then',
    description: 'formulas.formulaDescriptions.then',
    syntax: 'If(condition, Then(value), Else(value))',
    example: 'If(IsEmpty(Address), Then("No address"), Else("Address provided"))',
  },
  Sum: {
    name: 'Sum',
    description: 'formulas.formulaDescriptions.sum',
    syntax: 'Sum(value1, value2, ...)',
    example: 'Sum(Amount1, Amount2, Amount3)',
  },
  Avg: {
    name: 'Avg',
    description: 'formulas.formulaDescriptions.avg',
    syntax: 'Avg(value1, value2, ...)',
    example: 'Avg(Score1, Score2, Score3)',
  },
  Count: {
    name: 'Count',
    description: 'formulas.formulaDescriptions.count',
    syntax: 'Count(value1, value2, ...)',
    example: 'Count(Item1, Item2, Item3)',
  },
  Max: {
    name: 'Max',
    description: 'formulas.formulaDescriptions.max',
    syntax: 'Max(value1, value2, ...)',
    example: 'Max(Value1, Value2, Value3)',
  },
  Min: {
    name: 'Min',
    description: 'formulas.formulaDescriptions.min',
    syntax: 'Min(value1, value2, ...)',
    example: 'Min(Value1, Value2, Value3)',
  },
};

export class FormulaOperationHelper {
  static getOperationDefinition(operation: keyof typeof FormulaOperation): OperationDefinition | undefined {
    return FORMULA_OPERATION_DEFINITIONS[operation];
  }

  static getAllOperationDefinitions(): OperationDefinition[] {
    return Object.values(FORMULA_OPERATION_DEFINITIONS);
  }
}
